import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiClientError } from "@/lib/api-client";

/** Button field names mirror the Zernio SDK (`title`, `phone`) end to end. */
export interface AutomationButton {
  type: "url" | "postback" | "phone";
  title: string;
  url?: string;
  payload?: string;
  phone?: string;
}

export interface AutomationStats {
  delivered?: number;
  read?: number;
  linkClicks?: number;
  uniqueClicks?: number;
  trackedSends?: number;
}

/** "word" = whole-word match: keyword `app` no longer fires on `happy`. */
export type MatchMode = "exact" | "contains" | "word";

/** Instagram only. */
export interface AutomationAudience {
  /** Send followGate.message + a button to every commenter; the real DM is
   *  delivered only after they tap (no follow check). */
  tapToUnlock?: boolean;
  // Not edited in Pilot, but round-tripped so an edit keeps them.
  followerStatus?: "any" | "followers" | "non_followers";
  minFollowerCount?: number;
  whenUnknown?: "send" | "verify" | "skip";
}

export interface AutomationFollowGate {
  message?: string;
  buttonLabel?: string;
  notFollowingMessage?: string;
}

/** Postback payload that starts a Zernio workflow when the button is tapped. */
export const WORKFLOW_PAYLOAD_PREFIX = "zernio:workflow:";

export interface CommentAutomation {
  id: string;
  name: string;
  accountId: string;
  platform?: string;
  trigger: "comment" | "story_reply";
  keywords: string[];
  matchMode: MatchMode;
  excludeKeywords?: string[] | null;
  typoTolerance?: boolean | null;
  alsoMatchInDms?: boolean | null;
  audience?: AutomationAudience | null;
  followGate?: AutomationFollowGate | null;
  dmMessage: string;
  commentReply?: string | null;
  buttons?: AutomationButton[] | null;
  linkTracking: boolean;
  clickTag?: string | null;
  isActive: boolean;
  platformPostId?: string | null;
  postTitle?: string | null;
  stats?: AutomationStats;
}

export interface EligiblePost {
  id: string;
  platform: string;
  publishedAt: string;
  zernioPostId: string;
  pieceTitle: string | null;
}

export type AutomationLogStatus = "pending" | "sent" | "failed" | "skipped" | "gated";

/** Server-derived explanation of a failed delivery (see the API router). */
export type LogDiagnosis =
  | "non_follower_buttons"
  | "private_reply_consumed"
  | "platform_error";

export interface AutomationLog {
  id: string;
  commentId?: string;
  commenterId?: string;
  commenterName?: string;
  commentText?: string;
  /** "comment" or "dm" (alsoMatchInDms). */
  source?: string;
  status: AutomationLogStatus;
  error?: string;
  /** Raw platform error (Instagram/Facebook) behind a failed DM. */
  platformError?: { code?: number; subcode?: number; message?: string } | null;
  /** The comment's one private reply was already used. */
  privateReplyConsumed?: boolean;
  diagnosis?: LogDiagnosis | null;
  commentReplyStatus?: "sent" | "failed" | "skipped";
  commentReplyError?: string;
  createdAt: string;
}

/** Comments that reached the automation but matched none of its keywords. */
export interface AutomationMisses {
  total?: number;
  retentionDays?: number;
  samples?: Array<{
    commentText?: string;
    commenterName?: string;
    excludedBy?: string | null;
    at?: string;
  }>;
}

export interface AutomationLogsPage {
  logs: AutomationLog[];
  misses: AutomationMisses | null;
}

export interface AutomationWorkflow {
  id: string;
  name: string;
  status?: "draft" | "active" | "paused";
  platform?: string;
  accountId?: string;
}

export interface CreateAutomationBody {
  name: string;
  platform: "INSTAGRAM" | "FACEBOOK";
  trigger: "comment" | "story_reply";
  scheduledPostId?: string;
  keywords: string[];
  matchMode: MatchMode;
  excludeKeywords?: string[];
  typoTolerance?: boolean;
  alsoMatchInDms?: boolean;
  audience?: AutomationAudience;
  followGate?: AutomationFollowGate;
  dmMessage: string;
  commentReply?: string;
  buttons?: AutomationButton[];
  linkTracking: boolean;
  clickTag?: string;
}

export type UpdateAutomationBody = { automationId: string } & Partial<
  Omit<
    CreateAutomationBody,
    | "platform"
    | "trigger"
    | "scheduledPostId"
    | "commentReply"
    | "buttons"
    | "clickTag"
    | "audience"
    | "followGate"
  >
> & {
  audience?: AutomationAudience | null;
  followGate?: AutomationFollowGate | null;
  isActive?: boolean;
  // Nullable so the editor can CLEAR these. Omitting a key leaves the stored
  // value untouched, which made them impossible to remove once set.
  commentReply?: string | null;
  buttons?: AutomationButton[] | null;
  clickTag?: string | null;
};

const listKey = (brandId: string) => ["comment-automations", brandId];
const eligiblePostsKey = (brandId: string) => [
  "comment-automations-eligible-posts",
  brandId,
];
const logsKey = (brandId: string, automationId: string, status: string) => [
  "comment-automation-logs",
  brandId,
  automationId,
  status,
];

/** `null` means Zernio is not configured for this brand — a degraded state the
 *  UI renders as guidance, not an error. */
export function useCommentAutomations(brandId: string | null | undefined) {
  return useQuery<CommentAutomation[] | null, ApiClientError>({
    queryKey: listKey(brandId ?? ""),
    queryFn: async () => {
      try {
        const d = await apiFetch<{ automations: CommentAutomation[] }>(
          `/brands/${brandId}/comment-automations`,
        );
        return d.automations;
      } catch (err) {
        if (
          err instanceof ApiClientError &&
          err.code === "ZERNIO_NOT_CONFIGURED"
        ) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!brandId,
    retry: false,
    staleTime: 60_000,
  });
}

export function useEligiblePosts(
  brandId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery<EligiblePost[], ApiClientError>({
    queryKey: eligiblePostsKey(brandId ?? ""),
    queryFn: async () => {
      const d = await apiFetch<{ posts: EligiblePost[] }>(
        `/brands/${brandId}/comment-automations/eligible-posts`,
      );
      return d.posts;
    },
    enabled: !!brandId && enabled,
    retry: false,
    staleTime: 60_000,
  });
}

export function useCreateCommentAutomation(brandId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiClientError, CreateAutomationBody>({
    mutationFn: (body) =>
      apiFetch(`/brands/${brandId}/comment-automations`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey(brandId) });
    },
  });
}

export function useUpdateCommentAutomation(brandId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiClientError, UpdateAutomationBody>({
    mutationFn: ({ automationId, ...body }) =>
      apiFetch(`/brands/${brandId}/comment-automations/${automationId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey(brandId) });
    },
  });
}

export function useDeleteCommentAutomation(brandId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (automationId) =>
      apiFetch<void>(`/brands/${brandId}/comment-automations/${automationId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKey(brandId) });
    },
  });
}

export type LogStatusFilter = "all" | AutomationLogStatus;

/** `status` is sent to the API, not applied to the fetched page — filtering a
 *  single page client-side hides matches that fall outside it. */
export function useCommentAutomationLogs(
  brandId: string,
  automationId: string | null,
  status: LogStatusFilter = "all",
) {
  return useQuery<AutomationLogsPage, ApiClientError>({
    queryKey: logsKey(brandId, automationId ?? "", status),
    queryFn: async () => {
      const qs = new URLSearchParams({ limit: "50" });
      if (status !== "all") qs.set("status", status);
      const d = await apiFetch<{ logs: AutomationLog[]; misses?: AutomationMisses | null }>(
        `/brands/${brandId}/comment-automations/${automationId}/logs?${qs}`,
      );
      return { logs: d.logs ?? [], misses: d.misses ?? null };
    },
    enabled: !!brandId && !!automationId,
    retry: false,
    staleTime: 30_000,
  });
}

/** Zernio workflows a DM postback button can start. */
export function useAutomationWorkflows(brandId: string, enabled: boolean) {
  return useQuery<AutomationWorkflow[], ApiClientError>({
    queryKey: ["comment-automation-workflows", brandId],
    queryFn: async () => {
      const d = await apiFetch<{ workflows: AutomationWorkflow[] }>(
        `/brands/${brandId}/comment-automations/workflows`,
      );
      return d.workflows;
    },
    enabled: !!brandId && enabled,
    retry: false,
    staleTime: 60_000,
  });
}
